import * as React from 'react';
import {createPortal} from 'react-dom';
import {SizeContextProvider} from '@packages/utilities/styled';
import {
  OverlayContextProvider,
  addLayer,
  removeLayer,
  popSurface,
  useOverlay,
} from './OverlayContext';

type Props = {
  open: boolean;
  render: () => React.ReactElement;
  children?: React.ReactNode;
};

// Tip: Don’t use this component directly. Try `Layer` or `Positioner` instead.
export const Overlayer = ({open, render, children}: Props) => {
  const context = useOverlay();
  const [layer, setLayer] = React.useState<HTMLDivElement | null>(null);
  const layerRef = React.useRef<{
    element: HTMLDivElement;
    remove: ReturnType<typeof removeLayer>;
  } | null>(null);

  const removeCurrentLayer = React.useCallback((updateState: boolean) => {
    const currentLayer = layerRef.current;
    if (currentLayer) {
      currentLayer.remove(currentLayer.element);
      layerRef.current = null;
      if (updateState) {
        setLayer(null);
      }
    }
  }, []);

  // The chief reason we are using useLayoutEffect is to mimic the previous behavior
  // (using componentDidMount) as much as possible.
  React.useLayoutEffect(() => {
    let active = true;

    if (open && layerRef.current === null) {
      const remove = removeLayer(context);
      addLayer(context)((nextLayer) => {
        if (active) {
          layerRef.current = {element: nextLayer, remove};
          setLayer(nextLayer);
        } else {
          remove(nextLayer);
        }
      });
    } else if (!open) {
      removeCurrentLayer(true);
    }

    return () => {
      active = false;
    };
  }, [context, open, removeCurrentLayer]);

  React.useLayoutEffect(
    () => () => {
      removeCurrentLayer(false);
    },
    [removeCurrentLayer]
  );

  const nextContext = popSurface(context);
  return (
    <>
      {children || null}
      {layer &&
        createPortal(
          nextContext === context ? (
            <SizeContextProvider>{render()}</SizeContextProvider>
          ) : (
            <OverlayContextProvider value={nextContext}>
              <SizeContextProvider>{render()}</SizeContextProvider>
            </OverlayContextProvider>
          ),

          layer
        )}
    </>
  );
};

Overlayer.displayName = 'Overlayer';
